import {
  Branding,
  Config,
  DashboardConfig,
  Group,
} from "@financialcanvas/util/types";
import { createContext, useContext } from "react";

import buildConfig from "./config.json";

export const config: Config = {
  id: "INVALID",
  STUDIO_URI: "INVALID",
  STUDIO_S3_BUCKET: "INVALID",
  dashboards: [],
  title: "Financial Canvas Studio",
  branding: "sciurus",
};

/**
 * Base URL for API requests.
 *
 * Locally the webpack dev-server proxies `/api` and `/fc_api` to `STUDIO_URI`
 * (see `apps/studio/proxy.conf.js`), so requests must be relative to be
 * intercepted. There is no such proxy in deployed environments, where the API
 * lives on a different origin/subdomain, so we must target the absolute
 * `STUDIO_URI` there.
 */
export function apiBaseUrl(): string {
  const onDevProxy =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");
  return onDevProxy ? "" : config.STUDIO_URI;
}

export const getStudioFeatureFlagFromConfig = (flag: string) => {
  if (!("STUDIO_FEATURE_FLAGS" in config)) {
    return false;
  }

  if (!(config.STUDIO_FEATURE_FLAGS && flag in config.STUDIO_FEATURE_FLAGS)) {
    return false;
  }

  return config["STUDIO_FEATURE_FLAGS"][flag];
};

export function getBranding(branding?: string): Branding {
  const brandings: Record<string, Branding> = buildConfig.branding;
  return brandings[branding ?? config.branding ?? "sciurus"];
}

export function getGroups(): Group[] {
  const groups: Record<string, Omit<Group, "id">> = buildConfig.groups;
  return (config.groups ?? []).map((id) => ({
    id: id,
    ...groups[id],
  }));
}

const buildDashboards: Record<
  string,
  DashboardConfig & { component: string; keys: string[] }
> = buildConfig.dashboards;

export function getDashboardConfigs() {
  const buildConfigDashboards = Object.keys(buildDashboards);

  return config.dashboards
    .filter((dashboard) => buildConfigDashboards.includes(dashboard))
    .map((dashboard) => buildDashboards[dashboard]);
}

export function getDashboardConfig(dashboardName?: string) {
  const buildConfigDashboards = Object.keys(buildDashboards);

  return (
    config.dashboards
      .filter((dashboard) => buildConfigDashboards.includes(dashboard))
      .map((dashboard) => buildDashboards[dashboard])
      .find((d) => d.value === dashboardName) || {
      value: "",
      label: undefined, // Several components, including the header rely on this being undefined
      source: "",
      component: "",
      keys: [],
    }
  );
}

export const cfg = buildConfig;

export const ConfigContext = createContext<Config | null>(null);

export const useConfig = (): Config | null => useContext(ConfigContext);
